// P2-4 连线类型化（宽松校验）
//
// 规则表（source 输出类型 = 节点媒体类型）：
//   resource           → data.resourceType (image/video/audio/text)
//   generate           → data.generateType (image/video)
//   storyboardShot/Grid/Episode → image
//   seedanceMerge/motionStoryboard → video
//
// target 接受规则：
//   generate(image)              ← image / text
//   generate(video)              ← image / text / video（动态分镜参考视频）/ audio（参考音频）
//   storyboard* / motionStoryboard ← image / text
//   scriptToStoryboard           ← image（参考图）/ text（文本片段）
//   seedanceMerge                ← image / video
//   resource                     ← 同媒体类型（text 一律禁止连入 resource）
//   aiDirector                   ← 任意类型（reference 边作为对话上下文）；出边禁止
//   groupFrame / stickyNote      ← 禁入禁出（装饰节点，不参与连线）
//   其他/未知                     ← 宽松放行
//
// 注：一期 nodeNormalizer.js 里的 COMPATIBILITY/canConnect 只到 kind 粒度、
// 不区分节点角色（生成参考位 vs 资源互连），语义不吻合，故在此重写；
// 已存在的边不追溯清理（仅拦截新连线）。

export function getNodeOutputType(node) {
  const d = node?.data || {};
  const legacy = d.legacyType || node?.type;
  if (legacy === "resource") return d.resourceType || "any";
  if (legacy === "generate") return d.generateType || "any";
  if (legacy === "storyboardShot" || legacy === "storyboardGrid" || legacy === "storyboardEpisode" || legacy === "scriptToStoryboard") return "image";
  if (legacy === "seedanceMerge" || legacy === "motionStoryboard") return "video";
  return "any";
}

// 装饰节点（分组框/便签）：不参与任何连线
const NON_CONNECTABLE = new Set(["groupFrame", "stickyNote"]);

// 返回 null 表示允许；返回字符串表示拒绝原因（人话，给 toast）
export function getConnectionRejection(sourceNode, targetNode) {
  if (!sourceNode || !targetNode) return "连接的两端节点不存在";
  if (sourceNode.id === targetNode.id) return "不能连接节点自身";

  const srcLegacy = sourceNode.data?.legacyType || sourceNode.type;
  const dstLegacy = targetNode.data?.legacyType || targetNode.type;

  if (NON_CONNECTABLE.has(srcLegacy)) {
    return srcLegacy === "groupFrame" ? "分组框不参与连线" : "便签不参与连线";
  }
  if (NON_CONNECTABLE.has(dstLegacy)) {
    return dstLegacy === "groupFrame" ? "分组框不参与连线" : "便签不参与连线";
  }

  // 片5 AI 导演节点：只接收上下文输入（任意类型 reference 边），不输出连线
  if (srcLegacy === "aiDirector") {
    return "AI 导演节点只接收上下文输入，不输出连线";
  }

  const srcType = getNodeOutputType(sourceNode);

  switch (dstLegacy) {
    case "aiDirector":
      // 入边：任意 resource/storyboard*/generate/scriptToStoryboard 等均可作为对话上下文
      return null;
    case "generate": {
      const genType = targetNode.data?.generateType;
      if (srcType === "image" || srcType === "text" || srcType === "any") return null;
      if (srcType === "video") {
        return genType === "video" ? null : "视频节点不能作为图片生成的参考";
      }
      // 片3：音频节点允许连入视频生成节点（作为参考音频）
      if (srcType === "audio") {
        return genType === "video" ? null : "音频节点只能作为视频生成的参考音频";
      }
      return null;
    }
    case "storyboardShot":
    case "storyboardGrid":
    case "storyboardEpisode":
    case "motionStoryboard": {
      if (srcType === "image" || srcType === "text" || srcType === "any") return null;
      return "分镜节点只接受图片或文本作为参考";
    }
    case "scriptToStoryboard": {
      // 剧本转分镜节点：接受图片（角色/场景/道具参考图）与文本（剧本片段）作为参考输入
      if (srcType === "image" || srcType === "text" || srcType === "any") return null;
      return "剧本转分镜节点只接受图片或文本作为参考";
    }
    case "seedanceMerge": {
      if (srcType === "image" || srcType === "video" || srcType === "any") return null;
      return "视频合并节点只接受图片或视频输入";
    }
    case "resource": {
      const dstType = targetNode.data?.resourceType;
      if (srcType === "text") return "文本节点不能连接到资源节点";
      if (dstType && srcType !== "any" && srcType !== dstType) {
        return "资源节点只接受同类型素材相连";
      }
      return null;
    }
    default:
      return null; // 未知类型：宽松放行
  }
}

export function isValidConnectionPair(sourceNode, targetNode) {
  return getConnectionRejection(sourceNode, targetNode) === null;
}

// ReactFlow isValidConnection prop 工厂（连接过程中合法目标 Handle 会带 .valid class）
export function makeIsValidConnection(getNodes) {
  return (connection) => {
    const nodes = getNodes();
    const sourceNode = nodes.find((n) => n.id === connection.source);
    const targetNode = nodes.find((n) => n.id === connection.target);
    return isValidConnectionPair(sourceNode, targetNode);
  };
}
